/* eslint-disable no-nested-ternary */
import { FC, useMemo } from 'react';
import Select, { components, SingleValueProps } from 'react-select';
import { useSubscription } from 'react-apollo';
import { DocumentNode } from 'graphql';
import { useTranslation } from 'react-i18next';
import { GET_ALL_CURSOS } from '../../components/cursos/cursos.graphql';
import { selectorCustomReducerStyles, selectorCustomStyles } from '../../styles/SelectorStyles';
import { ReactComponent as CrossIcon } from '../../assets/cross-icon.svg';
import dropwdonOrangeIcon from '../../assets/dropdown-orange-icon.svg';

type Props = {
	name?: string;
	defaultValue?:
		| {
				value: string | number | boolean;
				label: string | number;
				menuLabel?: string;
				selectedLabel?: string;
		  }
		| null
		| {
				value: string | number | boolean;
				label: string | number;
				menuLabel?: string;
				selectedLabel?: string;
		  }[];
	onChange: (ev: any) => void;
	labelGetter?: (ev: any) => any;
	options?: { value: string | number | boolean; label: string | number; isDisabled?: boolean }[];
	getQuery?: DocumentNode;
	onValueClick?: any;
	onDelete?: any;
	alternativeStyle?: any;
	orangeDropdownIndicator?: any;
	isDisabled?: boolean;
	placeholder?: string;
	test?: boolean;
	isClearable?: boolean;
	customDropdownLabel?: boolean;
	customDisplayLabel?: boolean;
	customChild?: any;
	isMulti?: boolean;
	isSmall?: boolean;
	componentsCheck?: any;
	filterOption?: (option: any, rawInput: string) => boolean;
	noPortal?: boolean;
};

const SingleSelector: FC<Props> = ({
	name,
	defaultValue,
	onChange,
	labelGetter,
	options,
	getQuery,
	onDelete,
	alternativeStyle,
	orangeDropdownIndicator,
	isDisabled,
	placeholder,
	test,
	isClearable,
	customDropdownLabel,
	customDisplayLabel,
	customChild,
	isMulti,
	isSmall,
	componentsCheck,
	filterOption,
	noPortal
}) => {
	const { t } = useTranslation();
	const { data, loading, error } = useSubscription(getQuery || GET_ALL_CURSOS, { skip: !getQuery });

	const SingleValue = ({ children, ...props }: SingleValueProps<any>) => {
		return props.data.label ? (
			<components.SingleValue {...props} className="flex items-center">
				{customChild ? props.data.customChild : children}{' '}
				{!isClearable && <CrossIcon fill="#fff" className="ml-2 cursor-pointer" onClick={onDelete} />}
			</components.SingleValue>
		) : null;
	};

	const DropdownIndicator = (props: any) => {
		return (
			<components.DropdownIndicator {...props}>
				<img src={dropwdonOrangeIcon} alt="dropdown-orange-icon" />
			</components.DropdownIndicator>
		);
	};

	const selectorOptions = useMemo(
		() =>
			options ||
			(name &&
				data?.[name].map((val: any) => ({
					value: val.key,
					label: labelGetter && labelGetter(val)
				}))),
		[data, labelGetter, name, options]
	);

	const Option = (props: any) => {
		return (
			<components.Option {...props}>
				<div>
					{customDropdownLabel
						? t(props.data.customDropdownLabel)
						: customDisplayLabel
						? props.data.customDisplayLabel
						: props.data.label}
				</div>
			</components.Option>
		);
	};

	if (loading || error) {
		return null;
	}

	return (
		<Select
			menuPortalTarget={noPortal ? undefined : document.body} // <-- aquí la condición
			// menuPortalTarget={document.querySelector('body')}
			placeholder={placeholder ?? 'Selecciona...'}
			isDisabled={isDisabled}
			styles={
				isSmall
					? selectorCustomReducerStyles({
							isDisabled: isDisabled || test,
							disabledOptions: options?.some((opt) => opt.isDisabled) || false
					  })
					: alternativeStyle ||
					  selectorCustomStyles({
							isDisabled: isDisabled || test,
							disabledOptions: options?.some((opt) => opt.isDisabled) || false
					  })
			}
			value={defaultValue}
			name={name}
			components={
				componentsCheck
					? componentsCheck
					: {
							SingleValue,
							Option,
							...(orangeDropdownIndicator && { DropdownIndicator })
					  }
			}
			options={selectorOptions}
			onChange={onChange}
			isMulti={isMulti}
			noOptionsMessage={() => t('No hay opciones disponibles')}
			filterOption={filterOption}
		/>
	);
};

export default SingleSelector;
